import { useMemo } from 'react';
import { useRouter } from 'next/router';

import { Heading, IconButton, Paragraph, Popover, TextButton } from '@zillow/constellation-10';
import { IconLockClosedFilled } from '@zillow/constellation-icons';

import { useGetListingDetails } from '../../../../../hooks/queries/properties';

import { useAuth } from '../../../../AuthProvider';

import type { MouseEvent, ReactNode } from 'react';
import type { TextButtonPropsInterface } from '@zillow/constellation-10';

const STRING_TYPE = 'string';

type GetDisableEditInfoProps = {
  isFeed: boolean;
  allowFeedEdit: boolean;
  canUpdateListing: boolean;
  isArchived: boolean;
  isUpdateListingRestricted: boolean;
  disabledMessage: string | ReactNode;
};

const getDisableEditInfo = ({
  isFeed,
  allowFeedEdit,
  canUpdateListing,
  isArchived,
  isUpdateListingRestricted,
  disabledMessage,
}: GetDisableEditInfoProps): string | ReactNode => {
  switch (true) {
    case isFeed && !allowFeedEdit:
      return 'To change this information, go to your listing feed.';
    case !canUpdateListing:
      return `This information can’t be edited. For assistance, contact Customer Support.`;
    case isArchived:
      return 'To change this information, unarchive and update the listing.';
    case isUpdateListingRestricted:
      return `This information can’t be edited. For assistance, contact Customer Support.`;
    case !!disabledMessage:
      return disabledMessage;
    default:
      return '';
  }
};

const DEFAULT_EDIT_BUTTON_TEXT = 'Edit';

type Props = {
  allowFeedEdit?: boolean;
  isDisabled?: boolean;
  useLockIcon?: boolean;
  fieldLabel?: string;
  editButtonText?: string;
  popoverHeading?: string;
  value?: string;
  disabledMessage?: string | ReactNode;
  onEditClick: ((e: MouseEvent<HTMLButtonElement>) => void) | (() => void);
  dataEventId?: string;
  tone?: TextButtonPropsInterface['tone'];
  textStyle?: TextButtonPropsInterface['textStyle'];
  size?: TextButtonPropsInterface['size'];
};

const ContentSectionEditButton = ({
  allowFeedEdit = false,
  isDisabled: isDisabledFromParent = false,
  useLockIcon = false,
  tone = 'brand',
  fieldLabel,
  editButtonText = DEFAULT_EDIT_BUTTON_TEXT,
  textStyle = 'body-sm-bold',
  size = 'md',
  popoverHeading = '',
  value = '',
  disabledMessage = '',
  onEditClick,
  dataEventId,
}: Props): JSX.Element => {
  const router = useRouter();
  const { user } = useAuth();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const canUpdateListing = user?.features?.canUpdateListing ?? false;
  const isFeed = listingDetails?.isFeed ?? false;
  const isArchived = listingDetails?.isArchived ?? false;
  const isUpdateListingRestricted = listingDetails?.restrictions?.isUpdateListingRestricted ?? false;

  const isDisabled =
    isDisabledFromParent ||
    (isFeed && !allowFeedEdit) ||
    !canUpdateListing ||
    isArchived ||
    isUpdateListingRestricted ||
    useLockIcon;

  const disabledEditInfo = useMemo(() => {
    return getDisableEditInfo({
      isFeed,
      allowFeedEdit,
      canUpdateListing,
      isArchived,
      isUpdateListingRestricted,
      disabledMessage,
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [isDisabled]);
  const editLabel = fieldLabel ? `${editButtonText} ${fieldLabel}` : undefined;

  return isDisabled ? (
    <Popover
      header={<Heading level={2}>{popoverHeading || 'Edits are disabled'}</Heading>}
      body={typeof disabledEditInfo === STRING_TYPE ? <Paragraph>{disabledEditInfo}</Paragraph> : disabledEditInfo}
      tone="neutral"
      trigger={
        useLockIcon ? (
          <IconButton
            emphasis="bare"
            title={fieldLabel ? `${fieldLabel} is locked` : 'Editing is locked'}
            size="md"
            css={{ marginRight: '-300' }}
          >
            <IconLockClosedFilled />
          </IconButton>
        ) : (
          <TextButton
            aria-label={editLabel}
            data-testid={value || undefined}
            textStyle={textStyle}
            tone={tone}
            size={size}
            data-eventid={dataEventId}
            css={{ cursor: 'help', opacity: 'disabled' }}
          >
            {editButtonText}
          </TextButton>
        )
      }
    />
  ) : (
    <TextButton
      aria-label={editLabel}
      data-testid={value || undefined}
      textStyle={textStyle}
      tone={tone}
      onClick={onEditClick}
      value={value}
      size={size}
      data-eventid={dataEventId}
    >
      {editButtonText}
    </TextButton>
  );
};

export default ContentSectionEditButton;
